/** Deactivates an account; it stays in the history and nothing is paid to it any more. */
import { useState } from 'react';
import { ConfirmationModal, Modal } from '@insio/ui-toolkit';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { useToastHelpers } from '@/hooks/use-toast-helpers';
import { useBankAccountMutations } from '../hooks/use-bank-account-mutations';
import type { PartyBankAccount } from '../hooks/use-party-bank-accounts';
import {
  BankAccountError,
  bankAccountError,
  isStaleViewRefusal
} from '../utils/bank-account-error';
import { BankAccountSummary } from './BankAccountSummary';

interface DeactivateBankAccountModalProps {
  account: PartyBankAccount;
  onClose: () => void;
}

export function DeactivateBankAccountModal({ account, onClose }: DeactivateBankAccountModalProps) {
  const t = useFeatureTranslation('partyBankAccounts');
  const { showSuccess } = useToastHelpers();
  const { deactivate, refresh } = useBankAccountMutations();
  const [error, setError] = useState<BankAccountError | null>(null);
  const [saving, setSaving] = useState(false);

  const handleConfirm = async () => {
    setSaving(true);
    setError(null);
    try {
      await deactivate({ id: account.id });
      showSuccess(t('toast.deactivated'));
      onClose();
    } catch (err) {
      const refusal = bankAccountError(err);
      // The account changed meanwhile: the list shows it as it is now.
      if (isStaleViewRefusal(refusal)) await refresh();
      setError(refusal);
    } finally {
      setSaving(false);
    }
  };

  return (
    <ConfirmationModal
      open
      testId='deactivate-bank-account-modal'
      variant='danger'
      title={t('deactivate.title')}
      message={t('deactivate.message')}
      confirmLabel={t('deactivate.confirm')}
      cancelLabel={t('deactivate.cancel')}
      loading={saving}
      onConfirm={() => void handleConfirm()}
      onClose={onClose}
      body={
        <div className='flex flex-col gap-md'>
          <BankAccountSummary account={account} />
          {error && <Modal.Error role='alert'>{t(`errors.${error}`)}</Modal.Error>}
        </div>
      }
    />
  );
}
